/**
 * Sector-aware Profit & Loss ratio benchmark configuration.
 *
 * Thresholds are an initial heuristic screening policy, not peer-derived
 * facts and not investing recommendations.
 */

import {
    BENCHMARK_SOURCE_HEURISTIC,
    HEURISTIC_EFFECTIVE_FROM,
} from './sectorTaxonomy';

export const SCORED_METRIC_IDS = [
    'grossProfitMargin',
    'ebitdaMargin',
    'operatingProfitMarginPct',
    'netProfitMargin',
    'returnOnEquity',
    'returnOnCapitalEmployed',
    'returnOnAssets',
    'roicWaccSpread',
    'interestCoverageEbit',
    'interestCoverageEbitda',
    'dividendPayoutRatioScored',
];

export const NOT_SCORED_METRIC_IDS = [
    'eps',
    'earningsPerShare',
    'taxRate',
    'salesGrowth',
    'compoundedSalesGrowth',
    'compoundedProfitGrowth',
    'stockPriceCagr',
    'operatingRoic',
    'wacc',
    'standardRoic',
    'otherIncome',
    'interestExpense',
    'exceptionalItems',
    'normalizedOtherIncome',
    'weightedAverageCostOfCapital',
];

export const METRIC_LABELS = {
    grossProfitMargin: 'Gross Profit Margin (%)',
    ebitdaMargin: 'EBITDA Margin (%)',
    operatingProfitMarginPct: 'Operating Profit Margin (%)',
    netProfitMargin: 'Net Profit Margin (%)',
    returnOnEquity: 'Return on Equity (ROE) (%)',
    returnOnCapitalEmployed: 'Return on Capital Employed (ROCE) (%)',
    returnOnAssets: 'Return on Assets (ROA) (%)',
    roicWaccSpread: 'ROIC minus WACC (Economic Spread)',
    interestCoverageEbit: 'Interest Coverage – EBIT (x)',
    interestCoverageEbitda: 'Interest Coverage – EBITDA (x)',
    dividendPayoutRatioScored: 'Dividend Payout %',
};

export const PERCENTAGE_METRIC_IDS = new Set([
    'grossProfitMargin',
    'ebitdaMargin',
    'operatingProfitMarginPct',
    'netProfitMargin',
    'returnOnEquity',
    'returnOnCapitalEmployed',
    'returnOnAssets',
    'dividendPayoutRatioScored',
]);

export const PP_METRIC_IDS = new Set(['roicWaccSpread']);

export const PROFIT_LOSS_NOT_APPLICABLE_REASONS = {
    financialInstitutions:
        'Banks, NBFCs, insurers, brokers, and asset managers require sector-specific P&L measures such as NIM, credit cost, and capital adequacy — operating-company margins and returns are not comparable.',
    reitInvit:
        'REIT and InvIT profitability is judged on AFFO/FFO, occupancy, and distribution coverage rather than industrial operating margins and ROE/ROCE.',
};

export const NO_PAYOUT_NOTE =
    'No dividend payout in the evaluated period; payout quality is not applicable.';

export const ROE_LEVERAGE_WARNING =
    'ROE looks strong but leverage is elevated for this sector; part of the ROE may be financial rather than operating.';

export const ROIC_WACC_FLOOR_NOTE =
    'A negative ROIC−WACC spread means the business earned less than its cost of capital and is treated as requiring investigation regardless of sector.';

const HIGHER = 'higherIsBetter';
const BAND = 'band';

function heuristic(metricId, direction, thresholds) {
    let unit = 'multiple';
    if (PP_METRIC_IDS.has(metricId)) unit = 'pp';
    else if (PERCENTAGE_METRIC_IDS.has(metricId)) unit = 'percentage';
    return {
        source: BENCHMARK_SOURCE_HEURISTIC,
        effectiveFrom: HEURISTIC_EFFECTIVE_FROM,
        sampleSize: null,
        metricId,
        direction,
        thresholds,
        unit,
    };
}

function higher(metricId, strong, watch) {
    return heuristic(metricId, HIGHER, { strong, watch });
}

function band(metricId, strongMin, strongMax, watchMin, watchMax) {
    return heuristic(metricId, BAND, { strongMin, strongMax, watchMin, watchMax });
}

function archetypeMetrics({
    grossProfitMargin,
    ebitdaMargin,
    operatingProfitMarginPct,
    netProfitMargin,
    returnOnEquity,
    returnOnCapitalEmployed,
    returnOnAssets,
    roicWaccSpread,
    interestCoverageEbit,
    interestCoverageEbitda,
    dividendPayoutRatioScored,
}) {
    return {
        grossProfitMargin: higher('grossProfitMargin', grossProfitMargin[0], grossProfitMargin[1]),
        ebitdaMargin: higher('ebitdaMargin', ebitdaMargin[0], ebitdaMargin[1]),
        operatingProfitMarginPct: higher('operatingProfitMarginPct', operatingProfitMarginPct[0], operatingProfitMarginPct[1]),
        netProfitMargin: higher('netProfitMargin', netProfitMargin[0], netProfitMargin[1]),
        returnOnEquity: higher('returnOnEquity', returnOnEquity[0], returnOnEquity[1]),
        returnOnCapitalEmployed: higher('returnOnCapitalEmployed', returnOnCapitalEmployed[0], returnOnCapitalEmployed[1]),
        returnOnAssets: higher('returnOnAssets', returnOnAssets[0], returnOnAssets[1]),
        roicWaccSpread: higher('roicWaccSpread', roicWaccSpread[0], roicWaccSpread[1]),
        interestCoverageEbit: higher('interestCoverageEbit', interestCoverageEbit[0], interestCoverageEbit[1]),
        interestCoverageEbitda: higher('interestCoverageEbitda', interestCoverageEbitda[0], interestCoverageEbitda[1]),
        dividendPayoutRatioScored: band(
            'dividendPayoutRatioScored',
            dividendPayoutRatioScored[0],
            dividendPayoutRatioScored[1],
            dividendPayoutRatioScored[2],
            dividendPayoutRatioScored[3],
        ),
    };
}

/**
 * Initial heuristic thresholds by economic archetype.
 * Percentage values are percentage points (40 means 40%, not 0.40).
 */
export const ARCHETYPE_THRESHOLDS = {
    assetLightServices: archetypeMetrics({
        grossProfitMargin: [40, 25],
        ebitdaMargin: [22, 14],
        operatingProfitMarginPct: [18, 12],
        netProfitMargin: [14, 8],
        returnOnEquity: [22, 14],
        returnOnCapitalEmployed: [25, 15],
        returnOnAssets: [15, 8],
        roicWaccSpread: [6, 2],
        interestCoverageEbit: [12, 6],
        interestCoverageEbitda: [16, 8],
        dividendPayoutRatioScored: [25, 55, 10, 70],
    }),
    defensiveConsumer: archetypeMetrics({
        grossProfitMargin: [42, 28],
        ebitdaMargin: [18, 12],
        operatingProfitMarginPct: [14, 8],
        netProfitMargin: [10, 6],
        returnOnEquity: [18, 12],
        returnOnCapitalEmployed: [20, 12],
        returnOnAssets: [10, 6],
        roicWaccSpread: [4, 1.5],
        interestCoverageEbit: [10, 5],
        interestCoverageEbitda: [12, 6],
        dividendPayoutRatioScored: [35, 65, 20, 80],
    }),
    healthcareChemicals: archetypeMetrics({
        grossProfitMargin: [45, 30],
        ebitdaMargin: [22, 14],
        operatingProfitMarginPct: [16, 10],
        netProfitMargin: [12, 6],
        returnOnEquity: [16, 10],
        returnOnCapitalEmployed: [18, 12],
        returnOnAssets: [10, 6],
        roicWaccSpread: [4, 1.5],
        interestCoverageEbit: [8, 4],
        interestCoverageEbitda: [10, 5],
        dividendPayoutRatioScored: [20, 50, 10, 70],
    }),
    manufacturingIndustrial: archetypeMetrics({
        grossProfitMargin: [22, 14],
        ebitdaMargin: [14, 8],
        operatingProfitMarginPct: [12, 6],
        netProfitMargin: [8, 4],
        returnOnEquity: [15, 10],
        returnOnCapitalEmployed: [15, 10],
        returnOnAssets: [8, 4],
        roicWaccSpread: [3, 1],
        interestCoverageEbit: [6, 3],
        interestCoverageEbitda: [8, 4],
        dividendPayoutRatioScored: [20, 45, 10, 60],
    }),
    commodityCyclical: archetypeMetrics({
        grossProfitMargin: [18, 10],
        ebitdaMargin: [16, 8],
        operatingProfitMarginPct: [12, 5],
        netProfitMargin: [8, 3],
        returnOnEquity: [12, 8],
        returnOnCapitalEmployed: [12, 8],
        returnOnAssets: [6, 3],
        roicWaccSpread: [3, 1],
        interestCoverageEbit: [8, 4],
        interestCoverageEbitda: [10, 5],
        dividendPayoutRatioScored: [15, 40, 5, 55],
    }),
    longCycleProject: archetypeMetrics({
        grossProfitMargin: [18, 10],
        ebitdaMargin: [12, 6],
        operatingProfitMarginPct: [10, 4],
        netProfitMargin: [6, 2],
        returnOnEquity: [12, 8],
        returnOnCapitalEmployed: [10, 6],
        returnOnAssets: [4, 2],
        roicWaccSpread: [2, 0.5],
        interestCoverageEbit: [4, 2],
        interestCoverageEbitda: [5, 2.5],
        dividendPayoutRatioScored: [10, 35, 0, 50],
    }),
    regulatedNetworkCapex: archetypeMetrics({
        grossProfitMargin: [35, 20],
        ebitdaMargin: [30, 18],
        operatingProfitMarginPct: [18, 10],
        netProfitMargin: [10, 4],
        returnOnEquity: [12, 8],
        returnOnCapitalEmployed: [10, 6],
        returnOnAssets: [4, 2],
        roicWaccSpread: [2, 0.5],
        interestCoverageEbit: [3, 1.5],
        interestCoverageEbitda: [4, 2],
        dividendPayoutRatioScored: [40, 75, 25, 90],
    }),
    media: archetypeMetrics({
        grossProfitMargin: [35, 22],
        ebitdaMargin: [18, 10],
        operatingProfitMarginPct: [14, 8],
        netProfitMargin: [10, 5],
        returnOnEquity: [15, 10],
        returnOnCapitalEmployed: [15, 10],
        returnOnAssets: [8, 4],
        roicWaccSpread: [3, 1],
        interestCoverageEbit: [6, 3],
        interestCoverageEbitda: [8, 4],
        dividendPayoutRatioScored: [20, 50, 10, 70],
    }),
    notApplicable: null,
};
